// Modal for scheduling a new class session
import { useEffect, useState } from 'react'
import { Plus, XCircle } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Button } from '@/ui/button'
import { useClassSessionStore, useCourseStore } from '@/features/classes/stores'
import { useAuthStore } from '@/core/store/authStore'
import { ModalOverlay } from '@/ui/ModalOverlay'

import { inputClass } from '../utils/formatters'


export function CreateSessionModal({ onClose }: { onClose: () => void }) {
  const createSession = useClassSessionStore((s) => s.createSession)
  const courses = useCourseStore((s) => s.courses)
  const loadCourses = useCourseStore((s) => s.loadCourses)
  const authUser = useAuthStore((s) => s.user)
  const [courseId, setCourseId] = useState('')
  const [location, setLocation] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('17:00')
  const [maxAttendees, setMaxAttendees] = useState('20')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const activeCourses = courses.filter((c) => c.isActive !== false)

  useEffect(() => {
    void loadCourses()
  }, [loadCourses])

  // Default to the first available course once the catalog has loaded
  useEffect(() => {
    if (!courseId && activeCourses.length > 0) {
      setCourseId(activeCourses[0].slug)
    }
  }, [courseId, activeCourses])

  async function handleSubmit() {
    if (!courseId) {
      setError('Please select a course.')
      return
    }
    if (!location.trim()) {
      setError('Please complete all required fields.')
      return
    }
    const maxAttendeesNum = Number(maxAttendees)
    if (!Number.isFinite(maxAttendeesNum) || maxAttendeesNum < 1 || maxAttendeesNum > 500) {
      setError('Max attendees must be between 1 and 500.')
      return
    }
    if (endTime && startTime && endTime <= startTime) {
      setError('End time must be after start time.')
      return
    }
    const instructorName = authUser?.name?.trim() || authUser?.email?.trim() || 'Instructor' // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
    setSaving(true)
    setError('')
    try {
      await createSession({
        courseId,
        location: location.trim(),
        date,
        startTime: startTime || undefined,
        endTime: endTime || undefined,
        instructorName,
        maxAttendees: maxAttendeesNum,
      })
      onClose()
    } catch {
      setError('Could not save the class. Try again.')
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-navy">📝 Schedule New Class</CardTitle>
          <CardDescription>
            Complete the details. After saving, you can start the class when students arrive —
            a QR code will appear for them to register from their phone.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <XCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Course Type <span className="text-red-500">*</span>
            </label>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              disabled={activeCourses.length === 0}
              className={inputClass}
            >
              {activeCourses.length === 0 && (
                <option value="">Loading courses…</option>
              )}
              {activeCourses.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.name} (certificado válido por {c.validYears} año{c.validYears !== 1 ? 's' : ''})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Location <span className="text-red-500">*</span>
              <span className="text-slate-400 font-normal ml-1">(where the class takes place)</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Training Room A"
              className={inputClass}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">Date <span className="text-red-500">*</span></label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy mb-1">Start Time</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1">End Time</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1">
              Max Attendees <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min={1}
              max={500}
              value={maxAttendees}
              onChange={(e) => setMaxAttendees(e.target.value)}
              className={inputClass}
            />
            <p className="text-xs text-slate-400 mt-1">Maximum number of students for this session</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              <Plus className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Class'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </ModalOverlay>
  )
}
